import { PayPalButtons } from '@paypal/react-paypal-js'
import type { PayPalButtonsComponentProps } from '@paypal/react-paypal-js'
import { useEffect, useState } from 'react'

import { ApplePayButton } from '@/components/apple-pay-button'
import type { ApplePayStatus } from '@/components/apple-pay-button'
import { canUseApplePay } from '@/lib/apple-pay'
import { PAYPAL_BUTTON_STYLE } from '@/lib/paypal'
import { cn } from '@/lib/utils'

interface PayPalCheckoutButtonsProps
	extends Pick<PayPalButtonsComponentProps, 'createOrder' | 'onApprove'> {
	applePay: {
		amount: number
		label: string
		onBeforePay?: () => Promise<boolean> | boolean
		onApproved: (captureId: string) => Promise<void> | void
	}
}

/** Stops a hanging eligibility check from keeping PayPal hidden. */
const APPLE_PAY_REVEAL_TIMEOUT_MS = 5000

/**
 * PayPal wallet button (unchanged client-side create/capture) plus Apple Pay.
 * Apple Pay needs an eligibility round-trip before it may render, so on Apple devices
 * both buttons are held back and revealed together instead of popping in one by one.
 * Everywhere else there is no Apple Pay to wait for and PayPal shows immediately.
 */
export const PayPalCheckoutButtons = ({
	createOrder,
	onApprove,
	applePay,
}: PayPalCheckoutButtonsProps) => {
	const [applePayStatus, setApplePayStatus] = useState<ApplePayStatus>(() =>
		canUseApplePay() ? 'pending' : 'unavailable',
	)

	const isWaiting = applePayStatus === 'pending'

	useEffect(() => {
		if (!isWaiting) return

		const timer = setTimeout(() => setApplePayStatus('unavailable'), APPLE_PAY_REVEAL_TIMEOUT_MS)

		return () => clearTimeout(timer)
	}, [isWaiting])

	return (
		<div className="flex flex-col gap-3 w-full">
			<div
				className={cn(
					'w-full transition-opacity duration-200',
					isWaiting && 'opacity-0 pointer-events-none',
				)}>
				<PayPalButtons
					style={PAYPAL_BUTTON_STYLE}
					className="w-full mx-auto"
					createOrder={createOrder}
					onApprove={onApprove}
				/>
			</div>

			{/* Holds the Apple Pay button's space so revealing it does not shift the layout. */}
			{isWaiting && <div className="w-full h-12 lg:h-14" aria-hidden="true" />}

			<ApplePayButton
				amount={applePay.amount}
				label={applePay.label}
				onBeforePay={applePay.onBeforePay}
				onApproved={applePay.onApproved}
				onStatusChange={setApplePayStatus}
			/>
		</div>
	)
}
